import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { adminApi } from '@/api';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardHeader,
  EmptyState,
  ErrorState,
  Icon,
  LoadingState,
  Modal,
  Textarea,
  useToast,
} from '@/components/ui';
import { Fact, FactList, Prose } from '@/features/admin/Facts';
import { RecordHeader } from '@/features/admin/RecordHeader';
import { RemoveListingModal } from '@/features/admin/RemoveListingModal';
import { INTERNSHIP_TONES, VERIFICATION_TONES, humanizeStatus } from '@/features/admin/status';
import { APPLICATION_LABELS, APPLICATION_TONES } from '@/features/applications/status';
import { errorMessage } from '@/hooks/useApiError';
import { paths } from '@/routes/paths';
import { deadlineLabel, formatDate, formatStipend, humanizeWorkMode } from '@/utils/format';

/**
 * One listing, whole.
 *
 * The queue is a decision screen: it shows only what is waiting and drops a
 * listing the moment it is decided. This is the record -- reachable from the
 * company that posted it, from the queue, and from a support question weeks
 * later -- so the decision lives here too, next to what informs it.
 */
export function AdminInternshipDetailPage() {
  const { id } = useParams();
  const queryClient = useQueryClient();
  const toast = useToast();
  const [rejecting, setRejecting] = useState(false);
  const [takingDown, setTakingDown] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [reason, setReason] = useState('');

  const { data, isPending, isError, error, refetch } = useQuery({
    queryKey: ['admin-internship', id],
    queryFn: () => adminApi.getInternship(id),
  });

  function invalidate() {
    void queryClient.invalidateQueries({ queryKey: ['admin-internship', id] });
    void queryClient.invalidateQueries({ queryKey: ['admin-internships'] });
    void queryClient.invalidateQueries({ queryKey: ['admin-overview'] });
  }

  const approve = useMutation({
    mutationFn: () => adminApi.approveInternship(id),
    onSuccess: (internship) => {
      toast.success(`"${internship.title}" is live`);
      invalidate();
    },
    onError: (approveError) =>
      toast.error(errorMessage(approveError, 'Could not approve the internship')),
  });

  const reject = useMutation({
    mutationFn: (text) => adminApi.rejectInternship(id, text),
    onSuccess: () => {
      toast.success('Internship rejected');
      invalidate();
      setRejecting(false);
      setReason('');
    },
    onError: (rejectError) =>
      toast.error(errorMessage(rejectError, 'Could not reject the internship')),
  });

  const takeDown = useMutation({
    mutationFn: (text) => adminApi.closeInternship(id, text),
    onSuccess: () => {
      toast.success('Listing taken down');
      invalidate();
      setTakingDown(false);
      setReason('');
    },
    onError: (closeError) =>
      toast.error(errorMessage(closeError, 'Could not take the listing down')),
  });

  if (isPending) return <LoadingState label="Loading the internship…" />;
  if (isError) {
    return <ErrorState message={errorMessage(error)} onRetry={() => void refetch()} />;
  }

  const isPendingApproval = data.status === 'PENDING_APPROVAL';
  // Approve/reject decide whether it goes up; this is the decision about
  // one that already did, so it applies to exactly the live states.
  const canTakeDown = data.status === 'PUBLISHED' || isPendingApproval;
  // Removal is the stronger answer and also reaches a listing already taken down.
  const canRemove = canTakeDown || ['CLOSED', 'FILLED'].includes(data.status);
  const pipeline = Object.entries(data.applications_by_status ?? {}).filter(
    ([, count]) => count > 0,
  );
  const required = data.skills.filter((skill) => skill.is_required);
  const preferred = data.skills.filter((skill) => !skill.is_required);

  return (
    <div>
      <RecordHeader
        backTo={paths.admin.internships}
        backLabel="Internship approval"
        eyebrow="Internship"
        title={data.title}
        subtitle={[
          data.company_name,
          data.location,
          data.work_mode ? humanizeWorkMode(data.work_mode) : null,
        ]
          .filter(Boolean)
          .join(' · ')}
        badges={
          <>
            <Badge tone={INTERNSHIP_TONES[data.status] ?? 'neutral'}>
              {humanizeStatus(data.status)}
            </Badge>
            {/* Only on a published listing: published is not the same as
                open, because a passed deadline closes one without changing
                its status. Anywhere else it would restate the status badge. */}
            {data.status === 'PUBLISHED' && (
              <Badge tone={data.is_open ? 'success' : 'neutral'}>
                {data.is_open ? 'Open to applicants' : 'Deadline passed'}
              </Badge>
            )}
            {data.company_is_verified ? (
              <Badge tone="success">Verified company</Badge>
            ) : (
              <Badge dot>Unverified company</Badge>
            )}
            {!data.is_paid && <Badge tone="neutral">Unpaid</Badge>}
          </>
        }
        actions={
          <>
            {data.status === 'PUBLISHED' && (
              <Link
                to={paths.internshipDetail(data.slug)}
                className="text-brand-700 text-sm hover:underline"
              >
                Public page
              </Link>
            )}
            {isPendingApproval && (
              <>
                <Button
                  variant="success"
                  size="sm"
                  isLoading={approve.isPending}
                  disabled={!data.company_is_verified}
                  title={
                    data.company_is_verified
                      ? undefined
                      : 'Verify the company first — nothing goes live for an unverified company'
                  }
                  onClick={() => approve.mutate()}
                >
                  Approve
                </Button>
                <Button variant="secondary" size="sm" onClick={() => setRejecting(true)}>
                  Reject
                </Button>
              </>
            )}
            {canTakeDown && (
              <Button variant="ghostDanger" size="sm" onClick={() => setTakingDown(true)}>
                Take down
              </Button>
            )}
            {canRemove && (
              <Button variant="ghostDanger" size="sm" onClick={() => setRemoving(true)}>
                <Icon name="flag" size={15} />
                Remove as fraudulent
              </Button>
            )}
          </>
        }
        facts={[
          {
            label: 'Applications',
            value: data.applications_count,
            hint: `${data.positions} position${data.positions === 1 ? '' : 's'} open`,
          },
          { label: 'Views', value: data.views_count },
          {
            label: 'Deadline',
            value: data.application_deadline ? formatDate(data.application_deadline) : 'None',
            hint: deadlineLabel(data.application_deadline),
          },
          {
            label: 'Posted',
            value: formatDate(data.created_at),
            hint: data.published_at
              ? `Published ${formatDate(data.published_at)}`
              : `Last edited ${formatDate(data.updated_at)}`,
          },
        ]}
      />

      {data.status === 'REJECTED' && data.rejection_reason && (
        <Alert tone="warning" title="Rejected" className="mb-6">
          {data.rejection_reason} The company can edit the listing and submit it again.
        </Alert>
      )}

      {/* A closed listing carries the same field, and it matters more here:
          this one was public, and the reason is the record of why it stopped
          being. Whether the company closed it themselves or an admin did is in
          the audit log; the reason is only ever written by a moderator. */}
      {data.status === 'CLOSED' && data.rejection_reason && (
        <Alert tone="warning" title="Taken down" className="mb-6">
          {data.rejection_reason}
        </Alert>
      )}

      {/* Archived by an admin removal carries the moderator's reason; one the
          company archived itself has none. */}
      {data.status === 'ARCHIVED' && data.rejection_reason && (
        <Alert tone="error" title="Removed as fraudulent" className="mb-6">
          {data.rejection_reason}
        </Alert>
      )}

      <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
        <div className="min-w-0 space-y-6">
          <Card>
            <CardHeader title="The listing" />
            <FactList>
              <Fact
                label="Work mode"
                value={data.work_mode ? humanizeWorkMode(data.work_mode) : null}
              />
              <Fact label="Location" value={data.location} />
              <Fact
                label="Duration"
                value={data.duration_months ? `${data.duration_months} months` : null}
              />
              <Fact label="Starts" value={data.starts_on ? formatDate(data.starts_on) : null} />
              <Fact
                label="Stipend"
                value={
                  data.is_paid
                    ? formatStipend(data.stipend_min, data.stipend_max, data.stipend_currency)
                    : 'Unpaid'
                }
              />
              <Fact label="Positions" value={data.positions} />
              <Fact label="Industry" value={data.industry} />
              <Fact label="Category" value={data.job_category} />
              <Fact label="Slug" value={data.slug} />
            </FactList>

            <Prose title="Description">{data.description}</Prose>
            <Prose title="Responsibilities">{data.responsibilities}</Prose>
            <Prose title="Requirements">{data.requirements}</Prose>
            <Prose title="Perks">{data.perks}</Prose>
          </Card>

          {data.skills.length > 0 && (
            <Card>
              <CardHeader
                title="Skills"
                description="What the matching engine scores a candidate against."
              />
              {required.length > 0 && (
                <div>
                  <p className="text-ink-muted text-xs">Required</p>
                  <div className="mt-1.5 flex flex-wrap gap-1.5">
                    {required.map((skill) => (
                      <Badge key={skill.id} tone="brand">
                        {skill.name}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
              {preferred.length > 0 && (
                <div className="mt-4">
                  <p className="text-ink-muted text-xs">Preferred</p>
                  <div className="mt-1.5 flex flex-wrap gap-1.5">
                    {preferred.map((skill) => (
                      <Badge key={skill.id} tone="neutral">
                        {skill.name}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </Card>
          )}

          <Card>
            <CardHeader
              title="Applicants"
              description={
                data.applications.length < data.applications_count
                  ? `The ${data.applications.length} most recent of ${data.applications_count}.`
                  : undefined
              }
            />
            {data.applications.length === 0 ? (
              <EmptyState compact title="Nobody has applied to this listing yet" />
            ) : (
              <div className="overflow-x-auto">
                <table className="data-table w-full text-sm">
                  <thead className="text-ink-muted text-left">
                    <tr className="border-line border-b">
                      <th scope="col" className="py-2 pr-4 font-medium">
                        Student
                      </th>
                      <th scope="col" className="py-2 pr-4 font-medium">
                        University
                      </th>
                      <th scope="col" className="py-2 pr-4 font-medium">
                        Status
                      </th>
                      <th scope="col" className="py-2 pr-4 text-right font-medium">
                        Match
                      </th>
                      <th scope="col" className="py-2 pr-4 font-medium">
                        Applied
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.applications.map((row) => (
                      <tr key={row.id} className="border-line border-b last:border-0">
                        <td className="py-3 pr-4">
                          <Link
                            to={paths.admin.studentDetail(row.student_id)}
                            className="text-ink font-medium hover:underline"
                          >
                            {row.student_name}
                          </Link>
                          <span className="text-ink-muted block text-xs break-all">
                            {row.student_email}
                          </span>
                        </td>
                        <td className="text-ink-muted py-3 pr-4">{row.university ?? '—'}</td>
                        <td className="py-3 pr-4">
                          <Badge tone={APPLICATION_TONES[row.status] ?? 'neutral'}>
                            {APPLICATION_LABELS[row.status] ?? humanizeStatus(row.status)}
                          </Badge>
                        </td>
                        <td className="text-ink-muted py-3 pr-4 text-right tabular-nums">
                          {row.match_score == null ? '—' : `${Math.round(row.match_score)}%`}
                        </td>
                        <td className="text-ink-muted py-3 pr-4 whitespace-nowrap">
                          {formatDate(row.created_at)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader title="Company" />
            <Link
              to={paths.admin.companyDetail(data.company_id)}
              className="text-ink text-sm font-medium hover:underline"
            >
              {data.company_name}
            </Link>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <Badge tone={VERIFICATION_TONES[data.company_verification_status] ?? 'neutral'}>
                {humanizeStatus(data.company_verification_status)}
              </Badge>
              {data.company_location && (
                <span className="text-ink-muted text-xs">{data.company_location}</span>
              )}
            </div>
            <FactList columns={1} className="mt-4">
              <Fact label="Owner" value={data.company_owner_name} />
              <Fact label="Owner email" value={data.company_owner_email} />
            </FactList>
            <Link
              to={paths.companyPublic(data.company_slug)}
              className="text-brand-700 mt-3 inline-block text-sm hover:underline"
            >
              Public company page
            </Link>
          </Card>

          <Card>
            <CardHeader title="Moderation" />
            {/* Why this screen has buttons, or why it does not. Every listing
                a company publishes queues here; one from a company whose
                verification has since lapsed still cannot be approved. */}
            <p className="text-ink-muted text-sm">
              {isPendingApproval && !data.company_is_verified
                ? 'Waiting on the company. Nothing goes live until the company itself is verified — approve it under Verifications first, then come back to this listing. Rejecting is still possible.'
                : isPendingApproval
                  ? 'Waiting for a decision. Approving publishes it immediately; rejecting sends the company a reason to fix.'
                  : data.status === 'PUBLISHED'
                    ? 'Live. It cannot be approved or rejected now, but it can be taken down — the company is told why and the listing stops being findable.'
                    : 'Not waiting for a decision. Every listing a company publishes queues here for approval before it goes live.'}
            </p>
            <FactList columns={1} className="mt-4">
              <Fact label="Status" value={humanizeStatus(data.status)} showEmpty />
              <Fact
                label="Published"
                value={data.published_at ? formatDate(data.published_at) : null}
              />
              <Fact label="Last edited" value={formatDate(data.updated_at)} />
              <Fact label="Rejection reason" value={data.rejection_reason} />
            </FactList>
          </Card>

          <Card>
            <CardHeader title="Application pipeline" />
            {pipeline.length === 0 ? (
              <EmptyState compact title="No applications yet" />
            ) : (
              <dl className="space-y-1.5 text-sm">
                {pipeline.map(([status, count]) => (
                  <div key={status} className="flex items-center justify-between gap-3">
                    <dt className="text-ink-muted">
                      {APPLICATION_LABELS[status] ?? humanizeStatus(status)}
                    </dt>
                    <dd className="text-ink font-medium tabular-nums">{count}</dd>
                  </div>
                ))}
              </dl>
            )}
          </Card>
        </div>
      </div>

      <RemoveListingModal
        internship={removing ? data : null}
        onClose={() => setRemoving(false)}
        onRemoved={invalidate}
      />

      <Modal
        open={takingDown}
        title={`Take "${data.title}" down`}
        description="It stops being findable straight away and the company is told why. Applications already made are untouched, and the listing is not deleted."
        onClose={() => setTakingDown(false)}
      >
        <div className="space-y-4">
          <Textarea
            label="Reason"
            required
            rows={4}
            placeholder="The listing asks applicants to pay a deposit before an interview."
            value={reason}
            onChange={(event) => setReason(event.target.value)}
          />
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setTakingDown(false)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              disabled={reason.trim().length < 4}
              isLoading={takeDown.isPending}
              onClick={() => takeDown.mutate(reason.trim())}
            >
              Take it down
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        open={rejecting}
        title={`Reject "${data.title}"`}
        description="The company sees this reason and can fix and resubmit."
        onClose={() => setRejecting(false)}
      >
        <div className="space-y-4">
          <Textarea
            label="Reason"
            required
            rows={4}
            placeholder="The description does not explain what the intern will actually do."
            value={reason}
            onChange={(event) => setReason(event.target.value)}
          />
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setRejecting(false)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              disabled={reason.trim().length < 4}
              isLoading={reject.isPending}
              onClick={() => reject.mutate(reason.trim())}
            >
              Reject
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
